import type { ProductSelection } from '@zhiyue/product-center';
import { ProductSelector } from '@zhiyue/product-center/react';
import { InputNumber, Select, Space } from 'antd';
import '@zhiyue/product-center/style.css';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { ProductFormSection } from '@/modules/zy-crm/components/product-form-section';

type ProductOption = {
  label: string;
  value: string;
  raw?: Record<string, unknown>;
};

const NEW_PRODUCT_ROW_KEY = '__new_product__';

export type SharedProductRow = {
  key: string;
  offeringUuid?: string;
  offeringVersionUuid?: string;
  productCode?: string;
  versionNo?: string;
  standardPrice?: string;
  selectionSnapshot?: Record<string, unknown>;
  productUuid?: string;
  productName?: string;
  productType?: string;
  productUnit?: string;
  listPrice?: string;
  costPrice?: string;
  salePrice?: string;
  quantity?: string;
  discountPercent?: string;
  lineTotal?: string;
};

type SharedProductFormSectionEditingProps = {
  editingRowKey?: string;
  onBeginEdit: (row: SharedProductRow) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDraftChange: (
    patch:
      | Partial<SharedProductRow>
      | ((current: SharedProductRow) => Partial<SharedProductRow>),
  ) => void;
  onDeleteRow: (row: SharedProductRow) => void;
};

type SharedProductFormSectionProps = {
  title?: React.ReactNode;
  productNameTitle?: React.ReactNode;
  productTypeTitle?: React.ReactNode;
  productUnitTitle?: React.ReactNode;
  listPriceTitle?: React.ReactNode;
  costPriceTitle?: React.ReactNode;
  salePriceTitle?: React.ReactNode;
  quantityTitle?: React.ReactNode;
  discountTitle?: React.ReactNode;
  lineTotalTitle?: React.ReactNode;
  operationTitle?: React.ReactNode;
  addButtonText: string;
  productOptions: ProductOption[];
  productCenter?: {
    accessToken: string;
    memberUuid: string;
  };
  rows: SharedProductRow[];
  onSearchProducts: (keyword: string) => void;
  onAdd: () => void;
  showCostPrice?: boolean;
  showDiscount?: boolean;
  showOperation?: boolean;
  loading?: boolean;
  emptyText?: React.ReactNode;
  scroll?: { x?: number | string; y?: number | string };
} & SharedProductFormSectionEditingProps;

const hasText = (value: unknown) =>
  !(value === undefined || value === null || String(value).trim() === '');

export const toSharedNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return 0;
  }
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
};

export const toSharedAmount = (value: unknown, precision = 2) =>
  toSharedNumber(value).toFixed(precision);

export const toSharedOptionalAmount = (value: unknown, precision = 2) => {
  if (!hasText(value)) {
    return undefined;
  }
  return toSharedNumber(value).toFixed(precision);
};

export const fillSharedProductFromOption = (
  option?: ProductOption,
): Partial<SharedProductRow> => {
  const raw = option?.raw || {};
  return {
    productUuid: option?.value,
    productName: option?.label || '',
    productType: String(raw?.categoryName || raw?.typeName || ''),
    productUnit: String(raw?.unit || raw?.unitName || ''),
    listPrice: toSharedAmount(raw?.price),
    costPrice: toSharedOptionalAmount(raw?.costPrice),
    salePrice: toSharedAmount(raw?.price),
  };
};

export const normalizeSharedProductRows = (rows: SharedProductRow[]) =>
  rows.map((row) => {
    const quantity = toSharedNumber(row.quantity || 1);
    const salePrice = toSharedNumber(row.salePrice);
    const lineTotal = quantity * salePrice;
    return {
      ...row,
      productName: row.productName || '',
      productType: row.productType || undefined,
      productUnit: row.productUnit || undefined,
      listPrice: toSharedAmount(row.listPrice),
      costPrice: toSharedOptionalAmount(row.costPrice),
      salePrice: toSharedAmount(salePrice),
      quantity: toSharedAmount(quantity),
      discountPercent: hasText(row.discountPercent)
        ? toSharedAmount(row.discountPercent)
        : undefined,
      lineTotal: toSharedAmount(lineTotal),
    };
  });

const renderText = (value?: string, fallback = '-') => value || fallback;

const renderAmount = (value?: string, fallback = '0.00') => value || fallback;

const fillSharedProductFromSelection = (
  selection: ProductSelection,
): Partial<SharedProductRow> => {
  const standardPrice = toSharedAmount(
    selection.standardPrice?.price ??
      selection.selectionSnapshot.standardPrice?.price,
    6,
  );
  return {
    offeringUuid: selection.offeringUuid,
    offeringVersionUuid: selection.offeringVersionUuid,
    productCode: selection.productCode,
    versionNo: selection.versionNo || selection.versionCode,
    standardPrice,
    selectionSnapshot: selection.selectionSnapshot as unknown as Record<
      string,
      unknown
    >,
    productUuid: selection.offeringUuid,
    productName: selection.name,
    productType: selection.type,
    productUnit: selection.unit,
    listPrice: standardPrice,
    costPrice: standardPrice,
    salePrice: standardPrice,
  };
};

export function SharedProductFormSection({
  title = '产品明细',
  productNameTitle = '产品名称',
  productTypeTitle = '产品类型',
  productUnitTitle = '产品单位',
  listPriceTitle = '价格',
  costPriceTitle = '成本价',
  salePriceTitle = '售价',
  quantityTitle = '数量',
  discountTitle = '折扣',
  lineTotalTitle = '小计',
  operationTitle = '操作',
  addButtonText,
  productOptions,
  productCenter,
  rows,
  onSearchProducts,
  onAdd,
  showCostPrice = true,
  showDiscount = false,
  showOperation = true,
  loading,
  emptyText,
  scroll,
  editingRowKey,
  onBeginEdit,
  onSaveEdit,
  onCancelEdit,
  onDraftChange,
  onDeleteRow,
}: SharedProductFormSectionProps) {
  const [selectorRowKey, setSelectorRowKey] = React.useState<string>();
  const patchRow = (
    _row: SharedProductRow,
    patch: Partial<SharedProductRow>,
  ) => {
    onDraftChange(patch);
  };

  const isEditingRow = (row: SharedProductRow) => row.key === editingRowKey;
  const isNewEditingRow = editingRowKey === NEW_PRODUCT_ROW_KEY;

  const columns: ColumnsType<SharedProductRow> = [
    {
      title: productNameTitle,
      dataIndex: 'productUuid',
      width: 220,
      fixed: 'left',
      render: (_value, row) =>
        isEditingRow(row) ? (
          productCenter ? (
            <Space>
              <a
                onClick={() => setSelectorRowKey(row.key)}
                style={{ whiteSpace: 'nowrap' }}
              >
                {row.productName ? '更换版本' : '选择版本'}
              </a>
              {selectorRowKey === row.key ? (
                <ProductSelector
                  accessToken={productCenter.accessToken}
                  memberUuid={productCenter.memberUuid}
                  open
                  onSelect={(selection) => {
                    patchRow(row, fillSharedProductFromSelection(selection));
                    setSelectorRowKey(undefined);
                  }}
                  onCancel={() => setSelectorRowKey(undefined)}
                />
              ) : null}
            </Space>
          ) : (
            <Select
              showSearch
              allowClear
              value={row.productUuid}
              options={productOptions}
              filterOption={false}
              onSearch={(value) => onSearchProducts(value)}
              onChange={(value) => {
                const selected = productOptions.find(
                  (item) => item.value === value,
                );
                patchRow(row, fillSharedProductFromOption(selected));
              }}
              style={{ width: '100%' }}
            />
          )
        ) : (
          <span>{renderText(row.productName)}</span>
        ),
    },
    {
      title: productTypeTitle,
      dataIndex: 'productType',
      width: 130,
      render: (_value, row) => <span>{renderText(row.productType)}</span>,
    },
    {
      title: productUnitTitle,
      dataIndex: 'productUnit',
      width: 120,
      render: (_value, row) => <span>{renderText(row.productUnit)}</span>,
    },
    {
      title: listPriceTitle,
      dataIndex: 'listPrice',
      width: 120,
      render: (_value, row) => <span>{renderAmount(row.listPrice)}</span>,
    },
  ];

  if (showCostPrice) {
    columns.push({
      title: costPriceTitle,
      dataIndex: 'costPrice',
      width: 120,
      render: (_value, row) => <span>{row.costPrice || ''}</span>,
    });
  }

  columns.push(
    {
      title: salePriceTitle,
      dataIndex: 'salePrice',
      width: 120,
      render: (_value, row) =>
        isEditingRow(row) ? (
          <InputNumber
            value={toSharedNumber(row.salePrice)}
            min={0}
            precision={2}
            onChange={(value) =>
              patchRow(row, { salePrice: toSharedAmount(value) })
            }
            style={{ width: '100%' }}
          />
        ) : (
          <span>{renderAmount(row.salePrice)}</span>
        ),
    },
    {
      title: quantityTitle,
      dataIndex: 'quantity',
      width: 120,
      render: (_value, row) =>
        isEditingRow(row) ? (
          <InputNumber
            value={toSharedNumber(row.quantity)}
            min={0}
            precision={2}
            onChange={(value) =>
              patchRow(row, { quantity: toSharedAmount(value) })
            }
            style={{ width: '100%' }}
          />
        ) : (
          <span>{renderAmount(row.quantity)}</span>
        ),
    },
  );

  if (showDiscount) {
    columns.push({
      title: discountTitle,
      dataIndex: 'discountPercent',
      width: 120,
      render: (_value, row) =>
        isEditingRow(row) ? (
          <InputNumber
            value={toSharedNumber(row.discountPercent)}
            min={0}
            precision={2}
            onChange={(value) =>
              patchRow(row, { discountPercent: toSharedAmount(value) })
            }
            style={{ width: '100%' }}
          />
        ) : (
          <span>{row.discountPercent || '0.00'}%</span>
        ),
    });
  }

  columns.push({
    title: lineTotalTitle,
    dataIndex: 'lineTotal',
    width: 120,
    render: (_value, row) => <span>{renderAmount(row.lineTotal)}</span>,
  });

  if (showOperation) {
    columns.push({
      title: operationTitle,
      dataIndex: 'operation',
      width: 160,
      fixed: 'right',
      render: (_value, row) => {
        if (row.key === editingRowKey) {
          return (
            <Space size={12} wrap={false}>
              <a onClick={onSaveEdit}>保存</a>
              <a onClick={onCancelEdit}>取消</a>
              <a onClick={() => onDeleteRow(row)}>删除</a>
            </Space>
          );
        }
        return (
          <Space size={12} wrap={false}>
            {!isNewEditingRow ? (
              <a onClick={() => onBeginEdit(row)}>编辑</a>
            ) : null}
            <a onClick={() => onDeleteRow(row)}>删除</a>
          </Space>
        );
      },
    });
  }

  return (
    <ProductFormSection<SharedProductRow>
      title={title}
      rowKey="key"
      columns={columns}
      dataSource={rows}
      editingRowKey={editingRowKey}
      loading={loading}
      emptyText={emptyText}
      addButtonText={addButtonText}
      onAdd={onAdd}
      scroll={scroll}
    />
  );
}
